import { useEffect, useRef, useState } from "react";
import "./AuditLogs.css";
import Layout from "../../components/Layout";
import Avatar from "@mui/material/Avatar";
import PageHeader from "../../components/PageHeader/PageHeader";
import DataTable from "../../components/DataTable/DataTable";
import TablePagination from "../../components/DataTable/TablePagination";
import useCachedFetch from "../../hooks/useCachedFetch";

const PAGE_SIZE = 15;

const activityConfig = {
  Login: { className: "act-login", icon: "login" },
  Logout: { className: "act-logout", icon: "logout" },
  "Category Created": { className: "act-created", icon: "create_new_folder" },
  "Category Updated": {
    className: "act-updated",
    icon: "drive_file_rename_outline",
  },
  "Category Deleted": { className: "act-deleted", icon: "delete" },
  "Sub-category Created": {
    className: "act-created",
    icon: "create_new_folder",
  },
  "Sub-category Updated": {
    className: "act-updated",
    icon: "drive_file_rename_outline",
  },
  "Sub-category Deleted": { className: "act-deleted", icon: "delete" },
  "Skill Added": { className: "act-created", icon: "add_circle" },
  "Skill Deleted": { className: "act-deleted", icon: "remove_circle" },
  "User Deleted": { className: "act-deleted", icon: "person_remove" },
  "User Archived": { className: "act-archived", icon: "archive" },
  "User Blocked": { className: "act-blocked", icon: "block" },
  "User Unblocked": { className: "act-created", icon: "lock_open" },
  "User Recovered": { className: "act-updated", icon: "restart_alt" },
};

const activityOptions = [
  "Login",
  "Logout",
  "Category Created",
  "Category Updated",
  "Category Deleted",
  "Sub-category Created",
  "Sub-category Updated",
  "Sub-category Deleted",
  "Skill Added",
  "Skill Deleted",
  "User Blocked",
  "User Unblocked",
  "User Archived",
  "User Recovered",
  "User Deleted",
];

const getTargetIcon = (targetType) => {
  const map = {
    Category: "folder",
    "Sub-category": "folder_open",
    Skill: "psychology",
    User: "person",
  };
  return map[targetType] || "label";
};

const formatDateTime = (isoString) => {
  if (!isoString) return { date: "—", time: "" };
  const d = new Date(isoString);
  if (isNaN(d.getTime())) return { date: "—", time: "" };
  const day = String(d.getDate()).padStart(2, "0");
  const month = d.toLocaleString("en-GB", { month: "short" });
  const year = d.getFullYear();
  const time = d.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
  return { date: `${month} ${day}, ${year}`, time };
};

/* ── TABLE COLUMNS (static, so defined once) ── */
const columns = [
  {
    key: "date",
    header: (
      <span className="th-date">
        DATE &amp; TIME
        <span className="material-symbols-outlined th-sort-icon">
          arrow_upward
        </span>
      </span>
    ),
    width: "170px",
    skeleton: (
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div
          className="skeleton"
          style={{ width: 16, height: 16, borderRadius: "50%", flexShrink: 0 }}
        />
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          <div className="skeleton skeleton-text" style={{ width: 90 }} />
          <div className="skeleton skeleton-text short" style={{ width: 60 }} />
        </div>
      </div>
    ),
    render: (log) => {
      const { date, time } = formatDateTime(log.timestamp);
      return (
        <div className="date-wrapper">
          <span className="material-symbols-outlined date-clock-icon">
            schedule
          </span>
          <div>
            <div className="date-primary">{date}</div>
            <div className="date-time">{time}</div>
          </div>
        </div>
      );
    },
  },
  {
    key: "admin",
    header: "Admin account",
    width: "minmax(200px, 1fr)",
    skeleton: (
      <div className="user-cell">
        <div className="skeleton skeleton-avatar" />
        <div className="skeleton-text-group">
          <div className="skeleton skeleton-text" />
          <div className="skeleton skeleton-text short" />
        </div>
      </div>
    ),
    render: (log) => (
      <div className="user-cell">
        <Avatar
          src={log?.imageUrl}
          sx={{
            width: 34,
            height: 34,
            bgcolor: "#E5E9E5",
            color: "#006663",
            fontSize: "13px",
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          {log.adminName?.charAt(0)?.toUpperCase() || "A"}
        </Avatar>
        <div className="user-cell__text">
          <div className="name">{log.adminName || "Unknown"}</div>
          <div className="email">{log.adminEmail || "—"}</div>
        </div>
      </div>
    ),
  },
  {
    key: "activity",
    header: "Activity type",
    width: "minmax(170px, 0.8fr)",
    skeleton: (
      <div className="skeleton skeleton-badge" style={{ width: 130 }} />
    ),
    render: (log) => {
      const activity = activityConfig[log.activityType] || {
        className: "act-default",
        icon: "info",
      };
      return (
        <span className={`activity-badge ${activity.className}`}>
          <span className="material-symbols-outlined act-icon">
            {activity.icon}
          </span>
          {log.activityType}
        </span>
      );
    },
  },
  {
    key: "description",
    header: "Description",
    width: "minmax(220px, 1.4fr)",
    skeleton: (
      <div className="skeleton skeleton-text" style={{ width: "80%" }} />
    ),
    render: (log) => (
      <span className="desc-text">{log.description || "—"}</span>
    ),
  },
  {
    key: "target",
    header: "Target",
    width: "minmax(160px, 0.9fr)",
    skeleton: (
      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        <div className="skeleton skeleton-text" style={{ width: 110 }} />
        <div className="skeleton skeleton-text short" style={{ width: 70 }} />
      </div>
    ),
    render: (log) =>
      log.targetName && log.targetName !== "—" ? (
        <div className="target-cell">
          <span className="material-symbols-outlined target-folder-icon">
            {getTargetIcon(log.targetType)}
          </span>
          <div>
            <div className="target-name">{log.targetName}</div>
            <div className="target-type">({log.targetType})</div>
          </div>
        </div>
      ) : (
        <span className="no-target">—</span>
      ),
  },
  {
    key: "ip",
    header: "IP address",
    width: "130px",
    skeleton: (
      <div className="skeleton skeleton-text short" style={{ width: 90 }} />
    ),
    render: (log) => <span className="ip-text">{log.ipAddress || "—"}</span>,
  },
];

const AuditLogs = () => {
  const [filters, setFilters] = useState({
    activityType: "",
    sort: "newest",
    dateFrom: "",
    dateTo: "",
  });

  const {
    data: logs,
    total,
    loading,
    hasMore,
    page,
    fetch,
    nextPage,
    prevPage,
  } = useCachedFetch({
    cacheKey: "audit-logs",
    endpoint: "/audit-logs/get-all",
    params: filters,
    limit: PAGE_SIZE,
    ttl: 60000,
  });

  const fetchRef = useRef(fetch);
  useEffect(() => {
    fetchRef.current = fetch;
  }, [fetch]);
  useEffect(() => {
    fetchRef.current(0);
  }, [filters]);

  const setFilter = (key, value) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  return (
    <Layout>
      <div className="audit-page">
        <PageHeader
          title="Audit Logs"
          description="View and monitor all system activities performed by admin users."
          actions={
            <div className="audit-controls">
              {/* Activity type filter */}
              <div className="audit-filter-btn">
                <span className="material-symbols-outlined audit-filter-icon">
                  filter_list
                </span>
                <select
                  aria-label="Filter by activity type"
                  value={filters.activityType}
                  onChange={(e) => setFilter("activityType", e.target.value)}
                  className="audit-select-overlay"
                >
                  <option value="">All Activity Types</option>
                  {activityOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>

              {/* Date range */}
              <div className="audit-date-range">
                <span className="material-symbols-outlined audit-filter-icon">
                  calendar_today
                </span>
                <input
                  type="date"
                  aria-label="From date"
                  className="audit-date-input"
                  value={filters.dateFrom}
                  onChange={(e) => setFilter("dateFrom", e.target.value)}
                />
                <span className="date-sep">–</span>
                <input
                  type="date"
                  aria-label="To date"
                  className="audit-date-input"
                  value={filters.dateTo}
                  onChange={(e) => setFilter("dateTo", e.target.value)}
                />
                {(filters.dateFrom || filters.dateTo) && (
                  <span
                    className="material-symbols-outlined audit-clear-date"
                    onClick={() =>
                      setFilters((prev) => ({
                        ...prev,
                        dateFrom: "",
                        dateTo: "",
                      }))
                    }
                  >
                    close
                  </span>
                )}
              </div>

              {/* Filter pill button */}
              <button type="button" className="audit-filter-pill">
                <span
                  className="material-symbols-outlined"
                  style={{ fontSize: "16px" }}
                >
                  tune
                </span>
                Filter
              </button>
            </div>
          }
        />

        {/* SORT ROW */}
        <div className="audit-sort-row">
          <span className="sort-label">Sort by:</span>
          <button
            type="button"
            className="sort-toggle"
            onClick={() =>
              setFilter("sort", filters.sort === "newest" ? "oldest" : "newest")
            }
          >
            {filters.sort === "newest" ? "Newest First" : "Oldest First"}
            <span
              className="material-symbols-outlined"
              style={{ fontSize: "15px" }}
            >
              {filters.sort === "newest" ? "arrow_downward" : "arrow_upward"}
            </span>
          </button>
        </div>

        <DataTable
          columns={columns}
          rows={logs}
          loading={loading}
          skeletonRows={PAGE_SIZE}
          minWidth={1100}
          emptyMessage={
            <>
              <span className="material-symbols-outlined audit-empty-icon">
                manage_search
              </span>
              <span>No audit logs found</span>
            </>
          }
          footer={
            <TablePagination
              page={page}
              limit={PAGE_SIZE}
              total={total}
              count={logs.length}
              hasMore={hasMore}
              loading={loading}
              onPrev={prevPage}
              onNext={nextPage}
              noun="logs"
            />
          }
        />
      </div>
    </Layout>
  );
};

export default AuditLogs;
